import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  Typography,
  Box,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useFormik } from 'formik';

import CardWithProgressLoading from '../CardWithLoadingProgress';
import ButtonWithCirculaProgress from '../ButtonWithCirculaProgress';
import { useLazyRequest } from '../../hooks';
import { createPayment } from '../../../services/merchantsService';
import { getHelperText, hasError } from '../../../utils';
import { PAYMENT_COUPON_FLOW_OPTIONS } from '../../../constants/paymentCouponFlowOptions';

import {
  FIELDS_NAME,
  initialValues,
  validationSchema,
  isSelectedPix,
} from './utils';
import styles from './styles';

const useStyles = makeStyles(styles);

const PaymentCouponForm = ({ paymentSelectorPrefix, onSubmitSuccess }) => {
  const { t } = useTranslation('common');
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [, loadingCreatePayment, , requestCreatePayment] = useLazyRequest({
    request: createPayment,
    withPostSuccess: () => {
      enqueueSnackbar(t('paymentCoupon.generatePayment.successMessage'), {
        variant: 'success',
      });

      onSubmitSuccess?.();
    },
    withPostFailure: (response) => {
      enqueueSnackbar(response.errorData.message, { variant: 'error' });
    },
  });

  const executeGeneratePayment = useCallback(
    async (formValues, { setSubmitting, resetForm }) => {
      const flow = formValues[FIELDS_NAME.FLOW];

      const body = {
        flow,
        amount: formValues[FIELDS_NAME.AMOUNT],
      };

      if (isSelectedPix(flow)) {
        body.cpf = formValues[FIELDS_NAME.CPF];
      }

      if (flow === 'qr_ars_promotion') {
        body.flow = 'qr_ars';
        body.promotion = true;
      }

      await requestCreatePayment(body);
      setSubmitting(false);
      resetForm();
    },
    [requestCreatePayment],
  );

  const {
    values,
    touched,
    errors,
    submitForm,
    getFieldProps,
    handleChange,
    setFieldValue,
    dirty,
    isValid,
  } = useFormik({
    initialValues,
    validationSchema: validationSchema(t),
    onSubmit: executeGeneratePayment,
  });

  const handleChangeFlow = useCallback(
    async (event) => {
      handleChange(event);

      await setFieldValue(FIELDS_NAME.CPF, '', false);
    },
    [handleChange, setFieldValue],
  );

  return (
    <CardWithProgressLoading loading={false} className={classes.card}>
      <Box className={classes.cardContent}>
        <Typography
          component="h6"
          color="inherit"
          variant="caption"
          className={classes.cardContentTitle}
        >
          {t('paymentCoupon.title')}
        </Typography>

        <Box className={classes.cardContentInputs}>
          <FormControl
            error={hasError(touched, errors, FIELDS_NAME.FLOW)}
            variant="outlined"
          >
            <RadioGroup
              row
              {...getFieldProps(FIELDS_NAME.FLOW)}
              onChange={handleChangeFlow}
            >
              {PAYMENT_COUPON_FLOW_OPTIONS.map((flowOption) => (
                <FormControlLabel
                  key={`payment-coupon-flow-option-${flowOption.value}`}
                  value={flowOption.value}
                  control={<Radio color="secondary" />}
                  label={t(flowOption.labelTranslatedKey)}
                />
              ))}
            </RadioGroup>

            {hasError(touched, errors, FIELDS_NAME.FLOW) && (
              <FormHelperText>
                {getHelperText(touched, errors, FIELDS_NAME.FLOW, '')}
              </FormHelperText>
            )}
          </FormControl>

          <TextField
            fullWidth
            variant="outlined"
            type="number"
            label={t('paymentCoupon.amountWithPrefix', {
              prefix: paymentSelectorPrefix,
            })}
            autoComplete="off"
            color="secondary"
            InputLabelProps={{ shrink: true }}
            helperText={getHelperText(touched, errors, FIELDS_NAME.AMOUNT, '')}
            error={hasError(touched, errors, FIELDS_NAME.AMOUNT)}
            {...getFieldProps(FIELDS_NAME.AMOUNT)}
          />

          {isSelectedPix(values[FIELDS_NAME.FLOW]) && (
            <TextField
              fullWidth
              variant="outlined"
              label={t('paymentCoupon.cpf')}
              autoComplete="off"
              color="secondary"
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: 11 }}
              helperText={getHelperText(touched, errors, FIELDS_NAME.CPF, '')}
              error={hasError(touched, errors, FIELDS_NAME.CPF)}
              {...getFieldProps(FIELDS_NAME.CPF)}
            />
          )}
        </Box>
      </Box>

      <Box className={classes.cardActions}>
        <ButtonWithCirculaProgress
          variant="contained"
          color="secondary"
          fullWidth
          disabled={!dirty || !isValid}
          loading={loadingCreatePayment}
          label={t('paymentCoupon.generatePayment.title')}
          className={classes.cardActionsButton}
          handleButtonClick={submitForm}
        />
      </Box>
    </CardWithProgressLoading>
  );
};

PaymentCouponForm.propTypes = {
  paymentSelectorPrefix: PropTypes.string,
  onSubmitSuccess: PropTypes.func,
};

PaymentCouponForm.defaultProps = {
  paymentSelectorPrefix: 'pesos',
  onSubmitSuccess: undefined,
};

export default PaymentCouponForm;
